import {isFeatureEnabled} from 'feature-flags';

import {useMemo} from 'react';

import {isNil} from 'ramda';

import {CountryAlpha3CodeEnum} from '@dms/api/shared';
import {featureFlags} from '@dms/feature-flags';

import {FiltersType} from '../types/FiltersType';
import {createAuctionFilterBody} from '../utils/createAuctionFilterBody';
import {createFilterBody} from '../utils/createFilterBody';
import {isSuspiciousReasonFilterEnabled} from '../utils/isSuspiciousReasonFilterEnabled';
import {omitHiddenOriginCountryFilter} from '../utils/omitHiddenOriginCountryFilter';
import {omitHiddenSuspiciousReasonFilter} from '../utils/omitHiddenSuspiciousReasonFilter';
import {omitHiddenVanFilters} from '../utils/omitHiddenVanFilters';
import {SourcingFilterPresetsControl} from './useSourcingFilterPresets';

export function useSourcingFilterBodies(
  filters: FiltersType,
  preset: SourcingFilterPresetsControl,
  country: CountryAlpha3CodeEnum | undefined
) {
  const presetId = preset.isOutdated ? null : preset.appliedFilterPresetId;
  const presetFilters = preset.selectedFilterPreset?.filters;
  const isVanFiltersEnabled = isFeatureEnabled(featureFlags.SOURCING_VAN_FILTERS);
  const isOriginCountryFilterEnabled = isFeatureEnabled(
    featureFlags.SOURCING_ORIGIN_COUNTRY_FILTER
  );

  const isSuspiciousVehiclesEnabled = isFeatureEnabled(featureFlags.SOURCING_SUSPICIOUS_VEHICLES);
  const areSuspiciousReasonsEnabled = isSuspiciousReasonFilterEnabled();

  // Retain the stable request references consumed by the existing live-polling hooks.
  return useMemo(() => {
    if (isNil(country)) {
      return {classifieds: null, auctions: null};
    }
    const suspiciousFiltered = omitHiddenSuspiciousReasonFilter({
      filters,
      filterPresetId: presetId,
      presetFilters,
      isSuspiciousVehiclesEnabled,
      isSuspiciousReasonFilterEnabled: areSuspiciousReasonsEnabled,
    });
    const vanFiltered = omitHiddenVanFilters({
      ...suspiciousFiltered,
      presetFilters,
      isVanFiltersEnabled,
    });
    const classifieds = omitHiddenOriginCountryFilter({
      ...vanFiltered,
      presetFilters,
      isOriginCountryFilterEnabled,
    });
    return {
      classifieds: createFilterBody(classifieds.filters, classifieds.filterPresetId, country),
      auctions: createAuctionFilterBody(
        suspiciousFiltered.filters,
        suspiciousFiltered.filterPresetId,
        country
      ),
    };
  }, [
    filters,
    presetId,
    presetFilters,
    country,
    isVanFiltersEnabled,
    isOriginCountryFilterEnabled,
    isSuspiciousVehiclesEnabled,
    areSuspiciousReasonsEnabled,
  ]);
}
